import { buildHeadersWithWidgetToken } from '@elfsight/lib-utils';
import { meta } from '../../../../../constants';
import type {
  YoutubePlaylistItem,
  YoutubeResponseChannelListType,
  YoutubeResponsePlaylistItemType
} from '../types';

type ScopeType = 'channels' | 'videos' | 'playlistItems' | 'playlists';

export type YoutubeChannelType = 'channel' | 'user';

function buildURL(scope: ScopeType, params: string) {
  const apiURL = new URL('/api/v2/youtube', meta.storageURL);
  const preparedParams = new URLSearchParams({
    q: `/${scope}?${params}`
  });

  apiURL.search = preparedParams.toString();

  return apiURL.toString();
}

function extractVideoIDs(items: YoutubePlaylistItem[] = []) {
  return items.map((data) => {
    const {
      snippet: {
        resourceId: { videoId }
      }
    } = data;

    return videoId;
  });
}

// https://developers.google.com/youtube/v3/docs/videos/list
export function buildVideosApiURL(ids: string[]) {
  const scope = 'videos';
  const params = new URLSearchParams({
    part: 'id,snippet,statistics,status',
    id: ids.join()
  });

  return buildURL(scope, params.toString());
}

// https://developers.google.com/youtube/v3/docs/playlistItems/list
function buildPlaylistItemsApiURL(id: string, pageToken?: string) {
  const scope = 'playlistItems';
  const params = new URLSearchParams({
    part: 'snippet',
    playlistId: id,
    maxResults: '50'
  });

  if (pageToken) {
    params.append('pageToken', pageToken);
  }

  return buildURL(scope, params.toString());
}

function buildPlaylistsApiURL(id: string, searchByChannelID = false) {
  const scope = 'playlists';
  const params = new URLSearchParams({
    part: 'snippet'
  });
  const searchBy = searchByChannelID ? 'channelId' : 'id';

  params.append(searchBy, id);

  return buildURL(scope, params.toString());
}

function buildChannelApiURL(type: YoutubeChannelType, id: string) {
  const scope = 'channels';
  const params = new URLSearchParams({
    part: 'snippet,contentDetails'
  });

  switch (type) {
    case 'channel':
      params.append('id', id);
      break;
    case 'user':
      params.append('forUsername', id);
      break;
    default:
      throw new Error('Unknown channel type');
  }

  return buildURL(scope, params.toString());
}

async function fetchChannel(
  type: YoutubeChannelType,
  search: string,
  widgetToken: string
) {
  const apiURL = buildChannelApiURL(type, search);
  const headers = buildHeadersWithWidgetToken(widgetToken);
  const response = await fetch(apiURL, { headers });
  const resource: YoutubeResponseChannelListType = await response.json();

  return resource;
}

async function fetchPlaylists(
  search: string,
  searchByChannelID: boolean,
  widgetToken: string
) {
  const apiURL = buildPlaylistsApiURL(search, searchByChannelID);
  const headers = buildHeadersWithWidgetToken(widgetToken);
  const response = await fetch(apiURL, { headers });
  const resource: YoutubeResponsePlaylistItemType = await response.json();

  return resource;
}

async function fetchPlaylistItems(search: string, widgetToken: string) {
  const headers = buildHeadersWithWidgetToken(widgetToken);
  const items: YoutubePlaylistItem[] = [];
  const maxPages = 5;

  let apiURL: string = buildPlaylistItemsApiURL(search);
  let currentPage = 0;

  do {
    const response = await fetch(apiURL, { headers });
    const {
      nextPageToken,
      items: playlistItems
    }: YoutubeResponsePlaylistItemType = await response.json();

    items.push(...(playlistItems ?? []));

    apiURL = nextPageToken
      ? buildPlaylistItemsApiURL(search, nextPageToken)
      : '';
  } while (apiURL && ++currentPage <= maxPages);

  return items;
}

export async function getVideoIDFromPlaylist(
  playlistID: string,
  widgetToken: string
) {
  const playlistItems = await fetchPlaylistItems(playlistID, widgetToken);

  return extractVideoIDs(playlistItems);
}

async function getPlaylistByID(id: string, widgetToken: string) {
  const resource = await fetchPlaylists(id, false, widgetToken);

  if (!resource.items?.length) {
    throw new Error(
      "Couldn't find videos in this playlist. Double-check your link and try again."
    );
  }

  const {
    items: [playlist]
  } = resource;

  return playlist;
}

export async function getChannelByID(
  id: string,
  type: YoutubeChannelType,
  widgetToken: string
) {
  const resource = await fetchChannel(type, id, widgetToken);

  if (!resource?.items?.length) {
    throw new Error(
      "Couldn't find videos in this channel. Double-check your link and try again."
    );
  }

  const {
    items: [channel]
  } = resource;

  return channel;
}

export async function getPlaylistWithChannelByPlaylistID(
  id: string,
  widgetToken: string
) {
  const playlist = await getPlaylistByID(id, widgetToken);

  return {
    playlist,
    channel: await getChannelByID(
      playlist.snippet.channelId,
      'channel',
      widgetToken
    )
  };
}
